import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowDown, ArrowUp, Network } from "lucide-react";
import type { PlaylistBrowseItem } from "#shared/schemas/playlist.ts";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@web/components/ui/context-menu.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import { useGenresFlat, usePlaylistBrowse } from "@web/features/library/api.ts";
import { FilterBar } from "@web/features/library/FilterBar.tsx";
import {
  expandGenreSubtree,
  formatIdList,
  parseIdList,
} from "@web/features/library/filterUrl.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { formatDateTime } from "@web/lib/format.ts";
import { cn } from "@web/lib/utils.ts";

type SortKey =
  | "name"
  | "tracks_in_library"
  | "artist_count"
  | "album_count"
  | "statused_count"
  | "owner"
  | "added_at";

type SortState = { key: SortKey; dir: "asc" | "desc" };

const DEFAULT_SORT: SortState = { key: "tracks_in_library", dir: "desc" };

function matchHint(row: PlaylistBrowseItem): string | null {
  if (!row.match_via || row.match_via === "name") return null;
  const kind =
    row.match_via === "artist" ? "artist" : row.match_via === "album" ? "album" : "track";
  return row.match_label ? `via ${kind} · ${row.match_label}` : `via ${kind}`;
}

function compareRows(a: PlaylistBrowseItem, b: PlaylistBrowseItem, sort: SortState): number {
  const dir = sort.dir === "asc" ? 1 : -1;
  switch (sort.key) {
    case "name":
      return dir * (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
    case "tracks_in_library":
      return dir * (a.tracks_in_library - b.tracks_in_library);
    case "artist_count":
      return dir * (a.artist_count - b.artist_count);
    case "album_count":
      return dir * (a.album_count - b.album_count);
    case "statused_count":
      return dir * (a.statused_count - b.statused_count);
    case "owner": {
      const ao = a.owned_by_user ? 0 : 1;
      const bo = b.owned_by_user ? 0 : 1;
      if (ao !== bo) return dir * (ao - bo);
      return dir * (a.owner_name ?? "").localeCompare(b.owner_name ?? "", undefined, {
        sensitivity: "base",
      });
    }
    case "added_at": {
      const am = a.added_at ? Date.parse(a.added_at) : 0;
      const bm = b.added_at ? Date.parse(b.added_at) : 0;
      return dir * (am - bm);
    }
  }
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === sortKey;
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-1 px-3 text-xs font-semibold tracking-wider text-faint uppercase",
        align === "right" ? "justify-end" : "justify-start",
        active ? "text-muted" : "hover:text-muted",
      )}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active &&
        (sort.dir === "asc" ? (
          <ArrowUp className="size-3" aria-hidden />
        ) : (
          <ArrowDown className="size-3" aria-hidden />
        ))}
    </button>
  );
}

export function PlaylistsPage() {
  const openDetail = useOpenDetail();
  const navigate = useNavigate();
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const genreIds = useMemo(
    () => parseIdList(searchParams.get("genre")),
    [searchParams],
  );
  const [draftQ, setDraftQ] = useState(qParam);
  const [debouncedQ, setDebouncedQ] = useState(qParam);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const browse = usePlaylistBrowse(debouncedQ);
  const genres = useGenresFlat();

  useEffect(() => {
    setDraftQ(qParam);
  }, [qParam]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (draftQ.trim()) next.set("q", draftQ.trim());
      else next.delete("q");
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
      setDebouncedQ(draftQ.trim());
    }, 200);
    return () => window.clearTimeout(handle);
  }, [draftQ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const genreOptions = useMemo(
    () => (genres.data ?? []).map((g) => ({ id: g.id, label: g.name })),
    [genres.data],
  );
  const genreMatch = useMemo(() => {
    if (genreIds.length === 0) return null;
    return expandGenreSubtree(genreIds, genres.data ?? []);
  }, [genreIds, genres.data]);

  const rows = useMemo(() => {
    let next = [...(browse.data ?? [])];
    if (genreMatch) {
      next = next.filter((row) => (row.genre_ids ?? []).some((id) => genreMatch.has(id)));
    }
    next.sort((a, b) => {
      if (debouncedQ) {
        const aName = a.match_via === "name" ? 0 : 1;
        const bName = b.match_via === "name" ? 0 : 1;
        if (aName !== bName) return aName - bName;
      }
      return compareRows(a, b, sort);
    });
    return next;
  }, [browse.data, debouncedQ, sort, genreMatch]);

  function setGenreIds(ids: string[]) {
    const next = new URLSearchParams(searchParams);
    const formatted = formatIdList(ids);
    if (formatted) next.set("genre", formatted);
    else next.delete("genre");
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("genre");
    setDraftQ("");
    setDebouncedQ("");
    setSearchParams(next, { replace: true });
  }

  function toggleSort(key: SortKey) {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      const defaultDesc =
        key === "tracks_in_library" ||
        key === "artist_count" ||
        key === "album_count" ||
        key === "statused_count" ||
        key === "added_at";
      return { key, dir: defaultDesc ? "desc" : "asc" };
    });
  }

  function openSheet(playlist: PlaylistBrowseItem) {
    openDetail(
      { type: "playlist", id: playlist.id, label: playlist.name },
      { mode: "reset" },
    );
  }

  function openGrid(playlist: PlaylistBrowseItem) {
    void navigate(`/playlist/${encodeURIComponent(playlist.id)}`);
  }

  function openExplore(playlist: PlaylistBrowseItem) {
    void navigate(`/playlist/${encodeURIComponent(playlist.id)}/explore`);
  }

  function onRowClick(playlist: PlaylistBrowseItem) {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      openSheet(playlist);
    }, 250);
  }

  function onRowDoubleClick(playlist: PlaylistBrowseItem) {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    openGrid(playlist);
  }

  const isFiltered = Boolean(debouncedQ) || genreIds.length > 0;

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-col gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">Browse</p>
          <h1 className="text-2xl font-semibold tracking-tight">Playlists</h1>
          <p className="mt-1 text-sm text-muted">
            {browse.isLoading
              ? "Loading…"
              : isFiltered
                ? `${rows.length.toLocaleString()} matched`
                : `${rows.length.toLocaleString()} in your library`}
          </p>
        </div>

        <FilterBar
          searchValue={draftQ}
          onSearchChange={setDraftQ}
          searchPlaceholder="Search by playlist, artist, album, or track"
          genreOptions={genreOptions}
          genreIds={genreIds}
          onGenreIdsChange={setGenreIds}
          onClearAll={isFiltered ? clearFilters : undefined}
        />
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {browse.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full rounded-md" />
            ))}
          </div>
        )}

        {browse.isError && (
          <QueryErrorState
            error={browse.error}
            onRetry={() => void browse.refetch()}
            actionLabel="load playlists"
          />
        )}

        {browse.isSuccess && rows.length === 0 && (
          <EmptyState
            title={isFiltered ? "No matches" : "No playlists yet"}
            description={
              isFiltered
                ? "Try a different search, or clear the filter."
                : "Sync from Spotify to mirror playlists into CoyKoi."
            }
          />
        )}

        {browse.isSuccess && rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-line">
            <div
              className="grid border-b border-line bg-panel py-2"
              style={{
                gridTemplateColumns:
                  "minmax(12rem, 2fr) 5rem 5rem 5rem 6rem 6rem minmax(8rem, 1fr)",
              }}
            >
              <SortHeader label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortHeader
                label="Tracks"
                sortKey="tracks_in_library"
                sort={sort}
                onSort={toggleSort}
                align="right"
              />
              <SortHeader
                label="Artists"
                sortKey="artist_count"
                sort={sort}
                onSort={toggleSort}
                align="right"
              />
              <SortHeader
                label="Albums"
                sortKey="album_count"
                sort={sort}
                onSort={toggleSort}
                align="right"
              />
              <SortHeader label="Owner" sortKey="owner" sort={sort} onSort={toggleSort} />
              <SortHeader
                label="Statused"
                sortKey="statused_count"
                sort={sort}
                onSort={toggleSort}
                align="right"
              />
              <SortHeader label="Added" sortKey="added_at" sort={sort} onSort={toggleSort} />
            </div>

            <ul className="divide-y divide-line">
              {rows.map((playlist) => {
                const via = matchHint(playlist);
                return (
                  <li key={playlist.id}>
                    <ContextMenu>
                      <ContextMenuTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            "grid w-full items-center py-2 text-left text-sm transition-colors",
                            "hover:bg-row-hover focus-visible:bg-row-hover focus-visible:outline-none",
                          )}
                          style={{
                            gridTemplateColumns:
                              "minmax(12rem, 2fr) 5rem 5rem 5rem 6rem 6rem minmax(8rem, 1fr)",
                          }}
                          onClick={() => onRowClick(playlist)}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            onRowDoubleClick(playlist);
                          }}
                        >
                          <div className="flex min-w-0 items-center gap-3 px-3">
                            <EntityAvatar
                              src={playlist.cover_url}
                              alt=""
                              size="sm"
                              rounded="sm"
                            />
                            <span className="min-w-0">
                              <span className="block truncate font-medium">{playlist.name}</span>
                              {via && (
                                <span className="block truncate text-xs text-faint">{via}</span>
                              )}
                            </span>
                          </div>
                          <div className="px-3 text-right font-mono tabular-nums text-muted">
                            {playlist.tracks_in_library.toLocaleString()}
                          </div>
                          <div className="px-3 text-right font-mono tabular-nums text-muted">
                            {playlist.artist_count.toLocaleString()}
                          </div>
                          <div className="px-3 text-right font-mono tabular-nums text-muted">
                            {playlist.album_count.toLocaleString()}
                          </div>
                          <div className="truncate px-3 text-muted">
                            {playlist.owned_by_user ? "You" : (playlist.owner_name ?? "Other")}
                          </div>
                          <div className="px-3 text-right font-mono tabular-nums text-muted">
                            {playlist.statused_count.toLocaleString()} /{" "}
                            {playlist.tracks_in_library.toLocaleString()}
                          </div>
                          <div
                            className="truncate px-3 text-muted"
                            title="Earliest track added to this playlist"
                          >
                            {formatDateTime(playlist.added_at)}
                          </div>
                        </button>
                      </ContextMenuTrigger>
                      <ContextMenuContent>
                        <ContextMenuItem onSelect={() => openSheet(playlist)}>
                          Details
                        </ContextMenuItem>
                        <ContextMenuItem onSelect={() => openGrid(playlist)}>
                          Open tracks
                        </ContextMenuItem>
                        <ContextMenuSeparator />
                        <ContextMenuItem onSelect={() => openExplore(playlist)}>
                          <Network className="size-3.5" aria-hidden />
                          Explore in graph
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
